// src/feature-module/student/CVBuilderGuide.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown, FaChevronUp, FaBook } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const Section: React.FC<{ title: string; icon: string; children: React.ReactNode; defaultOpen?: boolean }> = ({ title, icon, children, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-xl">{icon}</span>
        <span className="flex-1 font-bold text-gray-800">{title}</span>
        {open ? <FaChevronUp className="text-gray-400" size={13} /> : <FaChevronDown className="text-gray-400" size={13} />}
      </button>
      {open && <div className="px-5 pb-5 text-sm text-gray-600 space-y-3 border-t border-gray-100">{children}</div>}
    </div>
  );
};

const Tip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 text-blue-800 text-xs leading-relaxed">
    <span className="flex-shrink-0 mt-0.5">💡</span>
    <span>{children}</span>
  </div>
);

const Warn: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5 text-amber-800 text-xs leading-relaxed">
    <span className="flex-shrink-0 mt-0.5">⚠️</span>
    <span>{children}</span>
  </div>
);

const Step: React.FC<{ n: number; text: string }> = ({ n, text }) => (
  <div className="flex gap-3 items-start">
    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#4A729A] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">{n}</span>
    <span className="leading-relaxed">{text}</span>
  </div>
);

const CVBuilderGuide: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaBook />}
        title="CV Anleitung"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "CV Builder", to: "/student/cv-builder" },
          { label: "Anleitung" },
        ]}
      />

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-4">

        {/* Intro card */}
        <div className="bg-gradient-to-r from-[#4A729A] to-[#2C4A6B] rounded-xl p-5 text-white">
          <h2 className="text-lg font-bold mb-1">Lebenslauf erstellen – Schritt für Schritt</h2>
          <p className="text-sm text-white/80 leading-relaxed">
            Mit dem G2G CV Builder kannst du einen professionellen deutschen Lebenslauf (Lebenslauf = CV) erstellen und als PDF herunterladen. Diese Anleitung erklärt jeden Schritt.
          </p>
        </div>

        {/* Step 0 – Format */}
        <Section title="1. Format & Farbthema wählen" icon="🎨" defaultOpen>
          <p className="pt-3 leading-relaxed">Wähle oben rechts dein bevorzugtes CV-Format:</p>
          <ul className="space-y-2 ml-1">
            <li><strong>G2G Classic (Empfohlen)</strong> – Klassisches deutsches Layout mit Seitenleiste. Ideal für alle Bewerber.</li>
            <li><strong>Modern Sidebar</strong> – Modernes Design mit farbiger linker Spalte. Für kreative Bereiche oder IT.</li>
            <li><strong>Executive</strong> – Formelles Design mit dunklem Header. Für Berufserfahrene oder Hochschulabsolventen.</li>
          </ul>
          <Tip>Für die meisten Bewerbungen in Deutschland ist G2G Classic am geeignetsten.</Tip>
          <p>Beim Modern Sidebar und Executive kannst du zusätzlich ein Farbthema auswählen (helle und dunkle Varianten verfügbar).</p>
        </Section>

        {/* Step 1 – Photo */}
        <Section title="2. Bewerbungsfoto" icon="📷">
          <p className="pt-3 text-sm text-gray-500 mb-4">
            Dein Bewerbungsfoto ist der erste Eindruck. So sollte es aussehen:
          </p>
          <div className="grid grid-cols-4 gap-3 mb-5">
            {[
              { src: "/assets/img/lectures/bewerbungsfoto_1.jpeg", label: "Männlich – Professionell" },
              { src: "/assets/img/lectures/bewerbungsfoto_2.jpeg", label: "Männlich – Freundlich" },
              { src: "/assets/img/lectures/bewerbungsfoto_3.jpeg", label: "Weiblich – Professionell" },
              { src: "/assets/img/lectures/bewerbungsfoto_4.jpeg", label: "Weiblich – Freundlich" },
            ].map((photo, i) => (
              <div key={i} className="relative rounded-xl overflow-hidden border-2 border-gray-200 hover:border-[#009fe3] transition-all shadow-sm">
                <div className="aspect-[3/4]">
                  <img src={photo.src} alt={photo.label} className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                  <span className="text-white text-[10px] font-semibold leading-tight block">{photo.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[
              { emoji: "😊", label: "Freundliches Auftreten" },
              { emoji: "🤝", label: "Einladende Gestik" },
              { emoji: "😁", label: "Lächeln" },
              { emoji: "🔄", label: "Rechte Schulter leicht nach vorne" },
            ].map((tip, i) => (
              <div key={i} className="flex items-center gap-2.5 p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm font-medium text-[#0369a1]">
                <span className="text-xl flex-shrink-0">{tip.emoji}</span>
                {tip.label}
              </div>
            ))}
          </div>
          <Tip>Weißer oder hellgrauer Hintergrund, gepflegte Kleidung (Hemd/Bluse), natürliche Beleuchtung. Das Foto sollte professionell wirken — kein Selfie!</Tip>
          <div className="mt-3 space-y-1.5">
            <Step n={1} text='Im CV Builder: "Upload Photo" klicken und Foto hochladen.' />
            <Step n={2} text="Foto in der Kreisvorschau ziehen, um es zu positionieren." />
            <Step n={3} text="Schieberegler nutzen, um das Foto zu vergrößern oder zu verkleinern." />
          </div>
        </Section>

        {/* Step 2 – Personal */}
        <Section title="3. Persönliche Informationen" icon="👤">
          <p className="pt-3 leading-relaxed">Fülle alle relevanten Felder aus:</p>
          <ul className="space-y-1.5 ml-1">
            <li><strong>Vorname / Nachname</strong> – Dein vollständiger Name. Wird nach dem ersten PDF-Download gesperrt.</li>
            <li><strong>Subtitle</strong> – Dein Studiengang oder Berufsbezeichnung, z. B. "Bachelor Informatik · TU Dresden"</li>
            <li><strong>Adresse</strong> – Deine aktuelle Adresse in Deutschland (oder im Herkunftsland).</li>
            <li><strong>Telefon / E-Mail</strong> – Erreichbarkeit für Arbeitgeber.</li>
            <li><strong>Geburtsdatum</strong> – Format: TT.MM.JJJJ, z. B. 15.03.2000</li>
            <li><strong>Geburtsort, Staatsangehörigkeit, Familienstand</strong> – Optional, aber in deutschen CVs üblich.</li>
          </ul>
          <Warn>Vorname und Nachname werden nach dem ersten PDF-Download dauerhaft gesperrt. Kontaktiere den Administrator, falls eine Änderung nötig ist.</Warn>
        </Section>

        {/* Step 3 – Sections */}
        <Section title="4. CV-Abschnitte hinzufügen" icon="📋">
          <p className="pt-3 leading-relaxed">Klicke auf <strong>"Add Section"</strong>, um neue Bereiche hinzuzufügen. Folgende Abschnitte stehen zur Verfügung:</p>
          <ul className="space-y-1.5 ml-1">
            <li><strong>Schulbildung</strong> – Deine Schulausbildung (SSC, HSC, Gymnasium etc.)</li>
            <li><strong>Studium / Hochschulbildung</strong> – Bachelor, Master, Diplom, Fachhochschule.</li>
            <li><strong>Berufserfahrung</strong> – Alle Arbeitsstellen nach der Ausbildung.</li>
            <li><strong>Praktikum</strong> – Praktika, Werkstudentenjobs, Hospitationen.</li>
            <li><strong>Ehrenamt / Freiwilligenarbeit</strong> – Ehrenamtliche Tätigkeiten.</li>
            <li><strong>Sprachkenntnisse</strong> – Sprachen mit Niveau (A1–C2, Muttersprache).</li>
            <li><strong>Fähigkeiten</strong> – IT-Kenntnisse, Software, Soft Skills.</li>
            <li><strong>Außerschulische Aktivitäten</strong> – Clubs, Sport, Projekte.</li>
          </ul>
          <Tip>Abschnitte lassen sich mit den Pfeilen ↑↓ in der Reihenfolge verschieben.</Tip>
        </Section>

        {/* Step 4 – Education */}
        <Section title="5. Schulbildung & Studium eintragen" icon="🎓">
          <p className="pt-3 leading-relaxed">Klicke innerhalb des Abschnitts auf <strong>"Add Entry"</strong>.</p>
          <ul className="space-y-1.5 ml-1">
            <li><strong>Zeitraum</strong> – z. B. "2018 – 2022" oder "09/2018 – 06/2022"</li>
            <li><strong>Titel</strong> – Abschluss oder Schulform, z. B. "Higher Secondary Certificate (HSC)"</li>
            <li><strong>Equivalent Class</strong> (optional) – z. B. "12. Klasse" oder "Abitur-Äquivalent"</li>
            <li><strong>Institution</strong> – Name der Schule oder Universität</li>
            <li><strong>Zusatzfelder</strong> – z. B. Note, Hauptfächer, Abschlussarbeit. Mit "Note"-Häkchen wird <em>(Deutsches Notensystem)</em> automatisch angezeigt.</li>
          </ul>
          <Tip>Für die Notenumrechnung: Nutze den <Link to="/student/grade-converter" className="text-[#4A729A] underline">Notenrechner</Link>, um deine bangladeschische Note ins deutsche System umzurechnen.</Tip>
        </Section>

        {/* Step 5 – Work */}
        <Section title="6. Berufserfahrung & Praktikum eintragen" icon="💼">
          <p className="pt-3 leading-relaxed">Der Eintrag für Berufserfahrung und Praktikum hat strukturierte Felder:</p>
          <ul className="space-y-1.5 ml-1">
            <li><strong>Positionsbezeichnung</strong> – Deine genaue Berufsbezeichnung, z. B. "Software Developer Intern"</li>
            <li><strong>Beschäftigungsart</strong> – Vollzeit, Teilzeit, Praktikum, Werkstudent etc.</li>
            <li><strong>Karrierestufe</strong> – Berufseinsteiger, Junior, Senior etc.</li>
            <li><strong>Kurzbeschreibung</strong> – 2–3 Sätze zu deinen Aufgaben und Erfolgen.</li>
            <li><strong>Unternehmen</strong> – Name der Firma oder Organisation.</li>
            <li><strong>Zeitraum</strong> – Start- und Endmonat/-jahr. Aktiviere "Bis heute" für aktuelle Stellen.</li>
            <li><strong>Zusatzfelder</strong> – Für spezifische Angaben wie Technologien, Projekte etc.</li>
          </ul>
          <Tip>Beschreibe Aufgaben mit aktiven Verben: "entwickelt", "koordiniert", "analysiert".</Tip>
        </Section>

        {/* Step 6 – Languages */}
        <Section title="7. Sprachkenntnisse" icon="🌐">
          <p className="pt-3 leading-relaxed">Trage jede Sprache mit ihrem Niveau ein:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs mt-1">
              <thead><tr className="bg-gray-50 border-b"><th className="text-left px-2 py-1.5 font-bold text-gray-500">Niveau</th><th className="text-left px-2 py-1.5 font-bold text-gray-500">Bedeutung</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {[["Muttersprache", "Native speaker"], ["C2", "Fließend / Mastery"], ["C1", "Fließend / Advanced"], ["B2", "Gute Kenntnisse / Upper-Intermediate"], ["B1", "Mittlere Kenntnisse / Intermediate"], ["A2", "Grundkenntnisse / Elementary"], ["A1", "Anfänger / Beginner"]].map(([lvl, desc]) => (
                  <tr key={lvl} className="hover:bg-gray-50"><td className="px-2 py-1.5 font-semibold text-gray-700">{lvl}</td><td className="px-2 py-1.5 text-gray-500">{desc}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <Tip>Deutschkenntnisse immer angeben! Auch A1 oder A2. Dein Sprachniveau zeigt Arbeitgebern dein Integrationspotenzial.</Tip>
        </Section>

        {/* Step 7 – Download */}
        <Section title="8. PDF herunterladen" icon="⬇️">
          <p className="pt-3 leading-relaxed">Klicke auf <strong>"Download PDF"</strong> oben rechts.</p>
          <div className="space-y-2">
            <Step n={1} text="Beim ersten Download werden Vorname, Nachname und Geburtsdatum dauerhaft gespeichert und gesperrt." />
            <Step n={2} text='Es öffnet sich der Browser-Druckdialog. Wähle "Als PDF speichern" als Drucker.' />
            <Step n={3} text='Stelle das Papierformat auf A4 und deaktiviere "Kopf- und Fußzeilen".' />
            <Step n={4} text='Aktiviere "Hintergrundgrafiken", damit Farben korrekt gedruckt werden.' />
          </div>
          <Warn>Stelle sicher, dass "Hintergrundgrafiken drucken" aktiviert ist – sonst fehlen Farben und Hintergründe im PDF.</Warn>
        </Section>

        {/* Tips */}
        <Section title="9. Tipps für den deutschen Lebenslauf" icon="✅">
          <ul className="pt-3 space-y-2 ml-1 leading-relaxed">
            <li>📄 <strong>1 Seite</strong> – Für Berufseinsteiger und Studienbewerber ist eine Seite ideal. Kürze, was nicht relevant ist.</li>
            <li>🗓️ <strong>Antichronologische Reihenfolge</strong> – Neuestes zuerst.</li>
            <li>📧 <strong>Professionelle E-Mail-Adresse</strong> – Verwende vorname.nachname@... statt Spitznamen.</li>
            <li>🚫 <strong>Kein "Lebenslauf" als Überschrift</strong> – Der CV Builder macht das automatisch richtig.</li>
            <li>🔢 <strong>Noten umrechnen</strong> – Nutze den <Link to="/student/grade-converter" className="text-[#4A729A] underline">Notenrechner</Link> für die Bayerische Formel.</li>
            <li>🌍 <strong>Lücken erklären</strong> – Visa-Wartezeiten, Sprachkurse oder Orientierungsphase kurz erwähnen.</li>
          </ul>
        </Section>

        <div className="flex gap-3 justify-center pt-2">
          <Link to="/student/cv-builder" className="px-5 py-2.5 bg-[#4A729A] text-white rounded-lg font-semibold text-sm hover:bg-[#3a5f84]">
            Zum CV Builder
          </Link>
          <Link to="/student/grade-converter" className="px-5 py-2.5 border border-[#4A729A] text-[#4A729A] rounded-lg font-semibold text-sm hover:bg-blue-50">
            Notenrechner
          </Link>
        </div>
      </main>
    </div>
  );
};

export default CVBuilderGuide;
